import { SiteChartsConfig } from '../../components/SiteCharts';

/**
 * Cell-Level Chart Configurations
 * 
 * These configurations define chart visualizations for individual LTE cells.
 * Unlike site-level charts (chartConfigs.ts) which aggregate data across all cells,
 * these charts show metrics for a specific cell.
 * 
 * API endpoints expect:
 * - site_name: The site/device serial number
 * - cell_name: The specific cell identifier
 * - time_range: Time range filter (lastWeek, lastMonth, all, custom)
 * - truncate: Aggregation level (minute, hour, day)
 * 
 * Database: LTE_CELL_KPI table
 */

/**
 * ERAB Establishment Success Rate (Cell Level)
 * 
 * Shows ERAB (E-RAB = E-UTRAN Radio Access Bearer) establishment metrics
 * for a specific cell, measuring the cell's ability to set up data bearers.
 */
export const erabCellChartConfig: SiteChartsConfig = {
  title: 'ERAB Establishment (Cell Level)',
  apiEndpoint: '/api/lte-cell/erab-establishment',
  csvFilename: 'erab_establishment_cell',
  chartImageFilename: 'erab_establishment_cell_chart',
  series: [
    {
      name: 'ERAB Establishment Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',
      dataKey: 'erab_success'
    },
    {
      name: 'ERAB Establishment Attempt',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',
      dataKey: 'erab_attempt'
    },
    {
      name: 'ERAB Establishment SR',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',
      dataKey: 'erab_success_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Data Accessibility %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'ERAB Success',
      dataKey: 'erab_success'
    },
    {
      header: 'ERAB Attempt',
      dataKey: 'erab_attempt'
    },
    {
      header: 'ERAB Establishment SR (%)',
      dataKey: 'erab_success_rate',
      isPercentage: true
    }
  ]
};

/**
 * VoLTE Establishment Success Rate (Cell Level)
 * 
 * Shows VoLTE (Voice over LTE) call establishment metrics for a specific cell,
 * measuring the cell's ability to set up voice calls over LTE.
 */
export const volteCellChartConfig: SiteChartsConfig = {
  title: 'VoLTE Establishment (Cell Level)',
  apiEndpoint: '/api/lte-cell/volte-establishment',
  csvFilename: 'volte_establishment_cell',
  chartImageFilename: 'volte_establishment_cell_chart',
  series: [
    {
      name: 'VoLTE Establishment Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',
      dataKey: 'volte_success'
    },
    {
      name: 'VoLTE Establishment Attempt',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',
      dataKey: 'volte_attempt'
    },
    {
      name: 'VoLTE Establishment SR',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',
      dataKey: 'volte_success_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Voice Accessibility %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'VoLTE Success',
      dataKey: 'volte_success'
    },
    {
      header: 'VoLTE Attempt',
      dataKey: 'volte_attempt'
    },
    {
      header: 'VoLTE Establishment SR (%)',
      dataKey: 'volte_success_rate',
      isPercentage: true
    }
  ]
};

/**
 * RRC Connection Establishment Success Rate (Cell Level)
 * 
 * Shows RRC (Radio Resource Control) connection establishment metrics
 * for a specific cell, measuring the cell's ability to establish signaling connections.
 */
export const rrcConnectionCellChartConfig: SiteChartsConfig = {
  title: 'RRC Connection Establishment (Cell Level)',
  apiEndpoint: '/api/lte-cell/rrc-connection',
  csvFilename: 'rrc_connection_cell',
  chartImageFilename: 'rrc_connection_cell_chart',
  series: [
    {
      name: 'RRC Connection Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',
      dataKey: 'rrc_success'
    },
    {
      name: 'RRC Connection Attempt',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',
      dataKey: 'rrc_attempt'
    },
    {
      name: 'RRC Connection SR',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',
      dataKey: 'rrc_success_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'RRC Accessibility %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'RRC Success',
      dataKey: 'rrc_success'
    },
    {
      header: 'RRC Attempt',
      dataKey: 'rrc_attempt'
    },
    {
      header: 'RRC Connection SR (%)',
      dataKey: 'rrc_success_rate',
      isPercentage: true
    }
  ]
};

/**
 * ERAB Drop Rate (Cell Level)
 * 
 * Shows ERAB drop metrics for a specific cell, measuring the cell's
 * ability to maintain established data bearers without dropping them.
 */
export const erabDropRateCellChartConfig: SiteChartsConfig = {
  title: 'ERAB Drop Rate (Cell Level)',
  apiEndpoint: '/api/lte-cell/erab-drop-rate',
  csvFilename: 'erab_drop_rate_cell',
  chartImageFilename: 'erab_drop_rate_cell_chart',
  series: [
    {
      name: 'ERAB Drops',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',
      dataKey: 'erab_drops'
    },
    {
      name: 'ERAB Drop Rate',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',
      dataKey: 'erab_drop_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Drop Rate %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'ERAB Drops',
      dataKey: 'erab_drops'
    },
    {
      header: 'ERAB Drop Rate (%)',
      dataKey: 'erab_drop_rate',
      isPercentage: true
    }
  ]
};

/**
 * VoLTE Drop Rate (Cell Level)
 * 
 * Shows VoLTE call drop metrics for a specific cell, measuring the cell's
 * ability to maintain established voice calls without dropping them.
 */
export const volteDropRateCellChartConfig: SiteChartsConfig = {
  title: 'VoLTE Drop Rate (Cell Level)',
  apiEndpoint: '/api/lte-cell/volte-drop-rate',
  csvFilename: 'volte_drop_rate_cell',
  chartImageFilename: 'volte_drop_rate_cell_chart',
  series: [
    {
      name: 'VoLTE Drops',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',
      dataKey: 'volte_drops'
    },
    {
      name: 'VoLTE Drop Rate',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',
      dataKey: 'volte_drop_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Drop Rate %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'VoLTE Drops',
      dataKey: 'volte_drops'
    },
    {
      header: 'VoLTE Drop Rate (%)',
      dataKey: 'volte_drop_rate',
      isPercentage: true
    }
  ]
};

/**
 * DL GTP Data Volume (Cell Level)
 * 
 * Shows downlink GTP (GPRS Tunneling Protocol) data volume for a specific cell.
 * Values are in GB, converted from bytes with 4 decimal precision.
 */
export const dlGtpDataVolumeCellChartConfig: SiteChartsConfig = {
  title: 'DL GTP Data Volume (Cell Level)',
  apiEndpoint: '/api/lte-cell/dl-gtp-data-volume',
  csvFilename: 'dl_gtp_data_volume_cell',
  chartImageFilename: 'dl_gtp_data_volume_cell_chart',
  series: [
    {
      name: 'DL GTP Data Volume',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',
      dataKey: 'dl_gtp_data_volume'
    }
  ],
  yAxes: [
    {
      name: 'DL GTP Data Volume (GB)',
      position: 'left',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: '',
      position: 'right',
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'DL GTP Data Volume (GB)',
      dataKey: 'dl_gtp_data_volume'
    }
  ]
};

/**
 * UL GTP Data Volume (Cell Level)
 * 
 * Shows uplink GTP (GPRS Tunneling Protocol) data volume for a specific cell.
 * Values are in GB, converted from bytes with 4 decimal precision.
 */
export const ulGtpDataVolumeCellChartConfig: SiteChartsConfig = {
  title: 'UL GTP Data Volume (Cell Level)',
  apiEndpoint: '/api/lte-cell/ul-gtp-data-volume',
  csvFilename: 'ul_gtp_data_volume_cell',
  chartImageFilename: 'ul_gtp_data_volume_cell_chart',
  series: [
    {
      name: 'UL GTP Data Volume',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',
      dataKey: 'ul_gtp_data_volume'
    }
  ],
  yAxes: [
    {
      name: 'UL GTP Data Volume (GB)',
      position: 'left',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: '',
      position: 'right',
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'UL GTP Data Volume (GB)',
      dataKey: 'ul_gtp_data_volume'
    }
  ]
};

/**
 * Handin Over S1 Interface (Cell Level)
 * 
 * Shows handin handover attempts and successes over S1 interface for a specific cell.
 */
export const handinOverS1CellChartConfig: SiteChartsConfig = {
  title: 'Handin Over S1 Interface (Cell Level)',
  apiEndpoint: '/api/lte-cell/handin-over-s1',
  csvFilename: 'handin_over_s1_cell',
  chartImageFilename: 'handin_over_s1_cell_chart',
  series: [
    {
      name: 'HandIn Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',  // Green color for success
      dataKey: 'handin_s1_successes'
    },
    {
      name: 'HandIn Attempts',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color for attempts
      dataKey: 'handin_s1_attempts'
    },
    {
      name: 'HandIn Success Rate',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',  // Blue color for rate
      dataKey: 'handin_success_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Success Rate %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'HandIn Success',
      dataKey: 'handin_s1_successes'
    },
    {
      header: 'HandIn Attempts',
      dataKey: 'handin_s1_attempts'
    },
    {
      header: 'HandIn Success Rate (%)',
      dataKey: 'handin_success_rate',
      isPercentage: true
    }
  ]
};

/**
 * Handout Over S1 Interface (Cell Level)
 * 
 * Shows handout handover attempts and successes over S1 interface for a specific cell.
 */
export const handoutOverS1CellChartConfig: SiteChartsConfig = {
  title: 'Handout Over S1 Interface (Cell Level)',
  apiEndpoint: '/api/lte-cell/handout-over-s1',
  csvFilename: 'handout_over_s1_cell',
  chartImageFilename: 'handout_over_s1_cell_chart',
  series: [
    {
      name: 'HandOut Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',  // Green color for success
      dataKey: 'handout_s1_successes'
    },
    {
      name: 'HandOut Attempts',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color for attempts
      dataKey: 'handout_s1_attempts'
    },
    {
      name: 'HandOut Success Rate',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',  // Blue color for rate
      dataKey: 'handout_success_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Success Rate %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'HandOut Success',
      dataKey: 'handout_s1_successes'
    },
    {
      header: 'HandOut Attempts',
      dataKey: 'handout_s1_attempts'
    },
    {
      header: 'HandOut Success Rate (%)',
      dataKey: 'handout_success_rate',
      isPercentage: true
    }
  ]
};

/**
 * Intra-frequency X1-HO (Cell Level)
 * 
 * Shows intra-frequency X1 handover attempts and successes for a specific cell.
 */
export const intraFreqX1HoCellChartConfig: SiteChartsConfig = {
  title: 'Intra-frequency X1-HO (Cell Level)',
  apiEndpoint: '/api/lte-cell/intra-freq-x1-ho',
  csvFilename: 'intra_freq_x1_ho_cell',
  chartImageFilename: 'intra_freq_x1_ho_cell_chart',
  series: [
    {
      name: 'Handover Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',  // Green color for success
      dataKey: 'intra_freq_x1_ho_successes'
    },
    {
      name: 'Handover Attempts',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color for attempts
      dataKey: 'intra_freq_x1_ho_attempts'
    },
    {
      name: 'Handover Success Rate',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',  // Blue color for rate
      dataKey: 'handover_success_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Success Rate %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'Handover Success',
      dataKey: 'intra_freq_x1_ho_successes'
    },
    {
      header: 'Handover Attempts',
      dataKey: 'intra_freq_x1_ho_attempts'
    },
    {
      header: 'Handover Success Rate (%)',
      dataKey: 'handover_success_rate',
      isPercentage: true
    }
  ]
};

/**
 * Inter-frequency X1-HO (Cell Level)
 * 
 * Shows inter-frequency X1 handover attempts and successes for a specific cell.
 */
export const interFreqX1HoCellChartConfig: SiteChartsConfig = {
  title: 'Inter-frequency X1-HO (Cell Level)',
  apiEndpoint: '/api/lte-cell/inter-freq-x1-ho',
  csvFilename: 'inter_freq_x1_ho_cell',
  chartImageFilename: 'inter_freq_x1_ho_cell_chart',
  series: [
    {
      name: 'Handover Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',  // Green color for success
      dataKey: 'inter_freq_x1_ho_successes'
    },
    {
      name: 'Handover Attempts',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color for attempts
      dataKey: 'inter_freq_x1_ho_attempts'
    },
    {
      name: 'Handover Success Rate',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',  // Blue color for rate
      dataKey: 'handover_success_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Success Rate %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'Handover Success',
      dataKey: 'inter_freq_x1_ho_successes'
    },
    {
      header: 'Handover Attempts',
      dataKey: 'inter_freq_x1_ho_attempts'
    },
    {
      header: 'Handover Success Rate (%)',
      dataKey: 'handover_success_rate',
      isPercentage: true
    }
  ]
};

/**
 * LTE Cell Availability (Cell Level)
 * 
 * Shows average LTE cell availability percentage for a specific cell.
 */
export const lteCellAvailabilityCellChartConfig: SiteChartsConfig = {
  title: 'LTE Cell Availability (Cell Level)',
  apiEndpoint: '/api/lte-cell/lte-cell-availability',
  csvFilename: 'lte_cell_availability_cell',
  chartImageFilename: 'lte_cell_availability_cell_chart',
  series: [
    {
      name: 'Cell Availability %',
      type: 'line',
      yAxisIndex: 0,
      color: 'success',  // Green color for availability
      dataKey: 'lte_cell_availability',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Cell Availability %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    },
    {
      name: '',
      position: 'right',
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'Cell Availability (%)',
      dataKey: 'lte_cell_availability',
      isPercentage: true
    }
  ]
};

/**
 * Maximum ENDC Connected UE Sessions (Cell Level)
 * 
 * Shows maximum ENDC (E-UTRA-NR Dual Connectivity) connected UE sessions
 * for a specific cell. ENDC enables simultaneous connectivity to LTE and 5G NR.
 */
export const maximumEndcCellChartConfig: SiteChartsConfig = {
  title: 'Maximum ENDC (Cell Level)',
  apiEndpoint: '/api/lte-cell/maximum-endc',
  csvFilename: 'maximum_endc_cell',
  chartImageFilename: 'maximum_endc_cell_chart',
  series: [
    {
      name: 'Maximum ENDC Connected UE Sessions',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Dark Teal (matching site-level)
      dataKey: 'maximum_endc_connected_ue_sessions'
    }
  ],
  yAxes: [
    {
      name: 'Sessions',
      position: 'left',
      nameGap: 70,
      formatter: '{value}'
    },
    {
      name: '',
      position: 'right',
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'Maximum ENDC Connected UE Sessions',
      dataKey: 'maximum_endc_connected_ue_sessions'
    }
  ]
};

/**
 * SgNB Addition Success Rate (Cell Level)
 * 
 * Shows SgNB (Secondary gNodeB) addition attempts, ACK success count, and success rate
 * for a specific cell. SgNB addition is part of ENDC setup procedure.
 */
export const sgNbAdditionCellChartConfig: SiteChartsConfig = {
  title: 'SgNB Addition (Cell Level)',
  apiEndpoint: '/api/lte-cell/sgnb-addition',
  csvFilename: 'sgnb_addition_cell',
  chartImageFilename: 'sgnb_addition_cell_chart',
  series: [
    {
      name: 'SgNB Addition ACK Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',  // Green color for success
      dataKey: 'sgnb_addition_ack_success'
    },
    {
      name: 'SgNB Addition Attempts',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color for attempts
      dataKey: 'sgnb_addition_attempts'
    },
    {
      name: 'SgNB Addition SR',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',  // Blue color for rate
      dataKey: 'sgnb_addition_sr',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Success Rate %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'SgNB Addition ACK Success',
      dataKey: 'sgnb_addition_ack_success'
    },
    {
      header: 'SgNB Addition Attempts',
      dataKey: 'sgnb_addition_attempts'
    },
    {
      header: 'SgNB Addition SR (%)',
      dataKey: 'sgnb_addition_sr',
      isPercentage: true
    }
  ]
};

/**
 * ============================================================================
 * NR CELL CHART CONFIGURATIONS
 * ============================================================================
 * 
 * Chart configurations for NR (5G) cell-level KPI metrics.
 * These charts display data for individual NR cells using the NR_FR2_CELL_KPI table.
 * 
 * Database: NR_FR2_CELL_KPI table
 * Technology: NR (5G)
 */

/**
 * SgNB Addition Success Rate (NR Cell Level)
 * 
 * Shows SgNB (Secondary gNodeB) addition metrics for a specific NR cell.
 * This is part of the EN-DC (E-UTRAN New Radio - Dual Connectivity) setup procedure.
 * 
 * Chart displays:
 * - Orange bars: SgNB Addition Request Attempts
 * - Green bars: SgNB Addition Request ACK Success
 * - Blue line: Success rate percentage (0-100%)
 * 
 * Success rate calculation: (ACK Success / Attempts) × 100
 * When attempts = 0, success rate = 100% (no failures)
 */
export const sgNbAdditionNrCellChartConfig: SiteChartsConfig = {
  title: 'SgNB Addition (NR Cell)',
  apiEndpoint: '/api/nr-cell/sgnb-addition',
  csvFilename: 'sgnb_addition_nr_cell',
  chartImageFilename: 'sgnb_addition_nr_cell_chart',
  series: [
    {
      name: 'SgNB Addition Attempts',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color for attempts
      dataKey: 'sgnb_addition_attempts'
    },
    {
      name: 'SgNB Addition ACK Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',  // Green color for success
      dataKey: 'sgnb_addition_ack_success'
    },
    {
      name: 'SgNB Addition SR',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',  // Blue color for rate
      dataKey: 'sgnb_addition_sr',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Success Rate %',
      position: 'left',
      min: 0,
      max: 100,
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'SgNB Addition Attempts',
      dataKey: 'sgnb_addition_attempts'
    },
    {
      header: 'SgNB Addition ACK Success',
      dataKey: 'sgnb_addition_ack_success'
    },
    {
      header: 'SgNB Addition SR (%)',
      dataKey: 'sgnb_addition_sr',
      isPercentage: true
    }
  ]
};

/**
 * Maximum ENDC Chart Configuration for NR Cell Level
 * 
 * Displays the maximum number of ENDC (E-UTRAN New Radio - Dual Connectivity) sessions
 * for a specific NR cell over time. Shows peak capacity usage.
 * 
 * Chart Type: Bar chart (matching Site E NR Maximum ENDC)
 * Data Source: NR_FR2_CELL_KPI table (filtered by Site Name and Cell Name)
 * Metric: Maximum Number of ENDC Sessions (using MAX aggregation)
 * 
 * Visual:
 * - Purple bars: Maximum ENDC sessions per time period
 */
export const maximumEndcNrCellChartConfig: SiteChartsConfig = {
  title: 'Maximum ENDC (NR Cell)',
  apiEndpoint: '/api/nr-cell/maximum-endc',
  csvFilename: 'maximum_endc_nr_cell',
  chartImageFilename: 'maximum_endc_nr_cell_chart',
  series: [
    {
      name: 'Max ENDC Sessions',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color (matching site-level Maximum ENDC)
      dataKey: 'maximum_endc_sessions'
    }
  ],
  yAxes: [
    {
      name: 'Sessions',
      position: 'left',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: '',
      position: 'right',
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'Max ENDC Sessions',
      dataKey: 'maximum_endc_sessions'
    }
  ]
};

/**
 * ENDC UE Session Drop Rate Chart Configuration for NR Cell Level
 * 
 * Shows ENDC UE session drop count and drop rate percentage for individual NR cells.
 * Data Source: NR_FR2_CELL_KPI table (filtered by Site Name and Cell Name)
 * Metrics: Call Drops, ENDC UE Sessions, Drop Rate
 * Formula: (Call Drops / ENDC UE Sessions) * 100
 * 
 * Visual:
 * - Orange bars: Call Drops count
 * - Blue line: Drop rate percentage
 */
export const endcUeSessionDropNrCellChartConfig: SiteChartsConfig = {
  title: 'ENDC UE Session Drop Rate (NR Cell)',
  apiEndpoint: '/api/nr-cell/endc-ue-session-drop',
  csvFilename: 'endc_ue_session_drop_nr_cell',
  chartImageFilename: 'endc_ue_session_drop_nr_cell_chart',
  series: [
    {
      name: 'ENDC UE Session Drop Count',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color (matching site-level and other drop metrics)
      dataKey: 'endc_ue_session_drop_count'
    },
    {
      name: 'ENDC UE Session Drop Rate',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',  // Blue color for rate
      dataKey: 'endc_ue_session_drop_rate',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'right',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Drop Rate %',
      position: 'left',
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'ENDC UE Session Drop Count',
      dataKey: 'endc_ue_session_drop_count'
    },
    {
      header: 'ENDC UE Session Drop Rate (%)',
      dataKey: 'endc_ue_session_drop_rate',
      isPercentage: true
    }
  ]
};

// ============================================================================
// NR Cell Availability (NR Cell) - Availability Group
// ============================================================================
// Metrics: NR_CellAvailabilityInfo_0, NR_CellAvailabilityInfo_1
// Formula: (NR_CellAvailabilityInfo_0 / NR_CellAvailabilityInfo_1) * 100
export const nrCellAvailabilityNrCellChartConfig: SiteChartsConfig = {
  title: 'NR Cell Availability (NR Cell)',
  apiEndpoint: '/api/nr-cell/nr-cell-availability',
  csvFilename: 'nr_cell_availability_nr_cell',
  chartImageFilename: 'nr_cell_availability_nr_cell_chart',
  series: [
    {
      name: 'NR Cell Availability',
      type: 'line',
      yAxisIndex: 0,
      color: 'total',  // Blue color (matching SGNB Addition SR)
      dataKey: 'nr_cell_availability',
      isPercentage: true
    }
  ],
  yAxes: [
    {
      name: 'Availability %',
      position: 'left',
      nameGap: 50,
      formatter: '{value}%',
      min: 0,
      max: 100
    },
    {
      name: '',
      position: 'right',
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'NR Cell Availability (%)',
      dataKey: 'nr_cell_availability',
      isPercentage: true
    }
  ]
};

/**
 * DL GTP Data Volume (NR Cell)
 * 
 * Shows downlink S1-U GTP data volume in GB for a specific NR cell.
 * Converts bytes to gigabytes (GB) for easier interpretation.
 * 
 * Database: NR_FR2_CELL_KPI table
 * Column: "Downlink S1-U GTP Data Volume (Bytes)"
 * Conversion: bytes / 1073741824 = GB
 */
export const dlGtpDataVolumeNrCellChartConfig: SiteChartsConfig = {
  title: 'DL GTP Data Volume (NR Cell)',
  apiEndpoint: '/api/nr-cell/dl-gtp-data-volume',
  csvFilename: 'dl_gtp_data_volume_nr_cell',
  chartImageFilename: 'dl_gtp_data_volume_nr_cell_chart',
  series: [
    {
      name: 'DL S1-U Data Volume (GB)',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color (matching Maximum ENDC)
      dataKey: 'dl_s1u_data_volume'
    }
  ],
  yAxes: [
    {
      name: 'Data Volume (GB)',
      position: 'left',
      nameGap: 50,
      formatter: '{value} GB'
    },
    {
      name: '',
      position: 'right',
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'DL S1-U Data Volume (GB)',
      dataKey: 'dl_s1u_data_volume'
    }
  ]
};

/**
 * UL GTP Data Volume (NR Cell)
 * 
 * Shows uplink S1-U GTP data volume in GB for a specific NR cell.
 * Converts bytes to gigabytes (GB) for easier interpretation.
 * 
 * Database: NR_FR2_CELL_KPI table
 * Column: "Uplink S1-U GTP Data volume (Bytes)" (note: lowercase 'volume')
 * Conversion: bytes / 1073741824 = GB
 */
export const ulGtpDataVolumeNrCellChartConfig: SiteChartsConfig = {
  title: 'UL GTP Data Volume (NR Cell)',
  apiEndpoint: '/api/nr-cell/ul-gtp-data-volume',
  csvFilename: 'ul_gtp_data_volume_nr_cell',
  chartImageFilename: 'ul_gtp_data_volume_nr_cell_chart',
  series: [
    {
      name: 'UL S1-U GTP Data Volume (GB)',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color 
      dataKey: 'ul_s1u_gtp_data_volume'
    }
  ],
  yAxes: [
    {
      name: 'Data Volume (GB)',
      position: 'left',
      nameGap: 50,
      formatter: '{value} GB'
    },
    {
      name: '',
      position: 'right',
      formatter: ''
    }
  ],
  tableColumns: [
    {
      header: 'UL S1-U GTP Data Volume (GB)',
      dataKey: 'ul_s1u_gtp_data_volume'
    }
  ]
};

/**
 * Intra CU Handover Success (NR Cell Level)
 * 
 * Shows Intra CU (Central Unit) handover metrics for a specific NR cell.
 * Handovers within the same CU are critical for maintaining service continuity
 * as UEs move between cells managed by the same central unit.
 * 
 * Metrics:
 * - Intra CU Handover Success: Total successful handovers within the CU
 * - Intra CU Handover Attempts: Total handover attempts within the CU
 * - Success Rate: (Successes / Attempts) × 100, or 100% if attempts = 0
 * 
 * Database columns from NR_FR2_CELL_KPI:
 * - "Intra CU Handover Successes"
 * - "Intra CU Handover Attempts"
 */
export const intraCuHandoverSuccessNrCellChartConfig: SiteChartsConfig = {
  title: 'Intra CU Handover Success (NR Cell)',
  apiEndpoint: '/api/nr-cell/intra-cu-handover-success',
  csvFilename: 'intra_cu_handover_success_nr_cell',
  chartImageFilename: 'intra_cu_handover_success_nr_cell_chart',
  series: [
    {
      name: 'Intra CU Handover Success',
      type: 'bar',
      yAxisIndex: 0,
      color: 'success',  // Green color for successes
      dataKey: 'intra_cu_handover_success'
    },
    {
      name: 'Intra CU Handover Attempts',
      type: 'bar',
      yAxisIndex: 0,
      color: 'warning',  // Orange color for attempts
      dataKey: 'intra_cu_handover_attempts'
    },
    {
      name: 'Intra CU Handover Success Rate (%)',
      type: 'line',
      yAxisIndex: 1,
      color: 'total',  // Purple color for percentage
      dataKey: 'intra_cu_handover_success_rate'
    }
  ],
  yAxes: [
    {
      name: 'Count',
      position: 'left',
      nameGap: 50,
      formatter: '{value}'
    },
    {
      name: 'Success Rate (%)',
      position: 'right',
      nameGap: 50,
      formatter: '{value}%'
    }
  ],
  tableColumns: [
    {
      header: 'Intra CU Handover Success',
      dataKey: 'intra_cu_handover_success'
    },
    {
      header: 'Intra CU Handover Attempts',
      dataKey: 'intra_cu_handover_attempts'
    },
    {
      header: 'Success Rate (%)',
      dataKey: 'intra_cu_handover_success_rate'
    }
  ]
};

// ============================================================
// SA (NR Standalone) Cell Chart Configurations
// ============================================================

// --- Accessibility (6 charts) ---

export const saRrcEstablishmentCellChartConfig: SiteChartsConfig = {
  title: 'RRC Establishment (NR Cell)',
  apiEndpoint: '/api/sa-cell/rrc-establishment-success-rate',
  csvFilename: 'sa_rrc_establishment_cell',
  chartImageFilename: 'sa_rrc_establishment_cell_chart',
  series: [
    { name: 'RRC Attempts',  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'rrc_establishment_attempts' },
    { name: 'RRC Successes', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'rrc_establishment_successes' },
    { name: 'RRC SR %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'rrc_establishment_successes_rate_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',          position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'RRC Attempts',  dataKey: 'rrc_establishment_attempts' },
    { header: 'RRC Successes', dataKey: 'rrc_establishment_successes' },
    { header: 'RRC SR (%)',    dataKey: 'rrc_establishment_successes_rate_percent', isPercentage: true }
  ]
};

export const saSignalingConnectionCellChartConfig: SiteChartsConfig = {
  title: 'Signaling Connection (NR Cell)',
  apiEndpoint: '/api/sa-cell/signaling-connection-success-rate',
  csvFilename: 'sa_signaling_connection_cell',
  chartImageFilename: 'sa_signaling_connection_cell_chart',
  series: [
    { name: 'Signaling Conn Attempts',  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'sig_conn_estab_attempts' },
    { name: 'Signaling Conn Successes', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'sig_conn_estab_successes' },
    { name: 'Signaling Conn SR %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'sig_conn_estab_success_rate_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',          position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'Signaling Conn Attempts',  dataKey: 'sig_conn_estab_attempts' },
    { header: 'Signaling Conn Successes', dataKey: 'sig_conn_estab_successes' },
    { header: 'Signaling Conn SR (%)',    dataKey: 'sig_conn_estab_success_rate_percent', isPercentage: true }
  ]
};

export const saDataAccessibilityCellChartConfig: SiteChartsConfig = {
  title: 'Data Accessibility (NR Cell)',
  apiEndpoint: '/api/sa-cell/data-accessibility-percent',
  csvFilename: 'sa_data_accessibility_cell',
  chartImageFilename: 'sa_data_accessibility_cell_chart',
  series: [
    { name: 'DRB Setup Attempts',   type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'drb_setup_attempts' },
    { name: 'DRB Setup Successes',  type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'drb_setup_successes' },
    { name: 'Data Accessibility %', type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'data_accessibility_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',                position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Data Accessibility %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'DRB Setup Attempts',     dataKey: 'drb_setup_attempts' },
    { header: 'DRB Setup Successes',    dataKey: 'drb_setup_successes' },
    { header: 'Data Accessibility (%)', dataKey: 'data_accessibility_percent', isPercentage: true }
  ]
};

export const saDataAccessibilityPer5QiCellChartConfig: SiteChartsConfig = {
  title: 'Data Accessibility per 5QI (NR Cell)',
  apiEndpoint: '/api/sa-cell/data-accessibility-per-5qi-percent',
  csvFilename: 'sa_data_accessibility_per_5qi_cell',
  chartImageFilename: 'sa_data_accessibility_per_5qi_cell_chart',
  series: [
    { name: 'DRB Setup Attempts per 5QI',   type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'drb_setup_attempts_per5qi' },
    { name: 'DRB Setup Successes per 5QI',  type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'drb_setup_successes_per5qi' },
    { name: 'Data Accessibility per 5QI %', type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'data_accessibility_per_5qi_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',                        position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Data Accessibility per 5QI %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'DRB Setup Attempts per 5QI',     dataKey: 'drb_setup_attempts_per5qi' },
    { header: 'DRB Setup Successes per 5QI',    dataKey: 'drb_setup_successes_per5qi' },
    { header: 'Data Accessibility per 5QI (%)', dataKey: 'data_accessibility_per_5qi_percent', isPercentage: true }
  ]
};

export const saQosFlowAccessibilityCellChartConfig: SiteChartsConfig = {
  title: 'QoS Flow Accessibility (NR Cell)',
  apiEndpoint: '/api/sa-cell/qos-flow-accessibility-percent',
  csvFilename: 'sa_qos_flow_accessibility_cell',
  chartImageFilename: 'sa_qos_flow_accessibility_cell_chart',
  series: [
    { name: 'QoS Flow Accessibility %', type: 'line', yAxisIndex: 0, color: 'info', dataKey: 'qos_flow_accessibility_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Accessibility %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' },
    { name: '',                position: 'right', formatter: '' }
  ],
  tableColumns: [
    { header: 'QoS Flow Accessibility (%)', dataKey: 'qos_flow_accessibility_percent', isPercentage: true }
  ]
};

export const saVonrAccessibilityCellChartConfig: SiteChartsConfig = {
  title: 'VoNR Accessibility (NR Cell)',
  apiEndpoint: '/api/sa-cell/vonr-accessibility-percent',
  csvFilename: 'sa_vonr_accessibility_cell',
  chartImageFilename: 'sa_vonr_accessibility_cell_chart',
  series: [
    { name: 'DRB Setup Attempts (5QI)',  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'drb_setup_attempts_5qi' },
    { name: 'DRB Setup Successes (5QI)', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'drb_setup_successes_5qi' },
    { name: 'VoNR Accessibility %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'vonr_accessibility_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',               position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'VoNR Accessibility %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'DRB Setup Attempts (5QI)',  dataKey: 'drb_setup_attempts_5qi' },
    { header: 'DRB Setup Successes (5QI)', dataKey: 'drb_setup_successes_5qi' },
    { header: 'VoNR Accessibility (%)',    dataKey: 'vonr_accessibility_percent', isPercentage: true }
  ]
};

// --- Retainability (4 charts) ---

export const saDataRetainabilityCellChartConfig: SiteChartsConfig = {
  title: 'Data Retainability (NR Cell)',
  apiEndpoint: '/api/sa-cell/data-retainability-percent',
  csvFilename: 'sa_data_retainability_cell',
  chartImageFilename: 'sa_data_retainability_cell_chart',
  series: [
    { name: 'DRB Drops',            type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'drb_drops' },
    { name: 'DRB Setup Successes',  type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'ng_sa_drb_setup_success' },
    { name: 'Data Retainability %', type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'data_retainability_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',                position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Data Retainability %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'DRB Drops',              dataKey: 'drb_drops' },
    { header: 'DRB Setup Successes',    dataKey: 'ng_sa_drb_setup_success' },
    { header: 'Data Retainability (%)', dataKey: 'data_retainability_percent', isPercentage: true }
  ]
};

export const saDataRetainabilityPer5QiCellChartConfig: SiteChartsConfig = {
  title: 'Data Retainability per 5QI (NR Cell)',
  apiEndpoint: '/api/sa-cell/data-retainability-per-5qi-percent',
  csvFilename: 'sa_data_retainability_per_5qi_cell',
  chartImageFilename: 'sa_data_retainability_per_5qi_cell_chart',
  series: [
    { name: '5QI Drops',                    type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'fiveqi_drops' },
    { name: 'DRB Setup Successes per 5QI',  type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'ng_sa_drb_setup_success_per_5qi' },
    { name: 'Data Retainability per 5QI %', type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'data_retainability_per_5qi_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',                        position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Data Retainability per 5QI %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: '5QI Drops',                      dataKey: 'fiveqi_drops' },
    { header: 'DRB Setup Successes per 5QI',    dataKey: 'ng_sa_drb_setup_success_per_5qi' },
    { header: 'Data Retainability per 5QI (%)', dataKey: 'data_retainability_per_5qi_percent', isPercentage: true }
  ]
};

export const saVonrRetainabilityCellChartConfig: SiteChartsConfig = {
  title: 'VoNR Retainability (NR Cell)',
  apiEndpoint: '/api/sa-cell/vonr-retainability-percent',
  csvFilename: 'sa_vonr_retainability_cell',
  chartImageFilename: 'sa_vonr_retainability_cell_chart',
  series: [
    { name: 'VoNR Drops',                  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'vonr_drops' },
    { name: 'DRB Setup Successes per 5QI', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'ng_sa_drb_setup_success_per_5qi' },
    { name: 'VoNR Retainability %',        type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'vonr_retainability_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',                position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'VoNR Retainability %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'VoNR Drops',                  dataKey: 'vonr_drops' },
    { header: 'DRB Setup Successes per 5QI', dataKey: 'ng_sa_drb_setup_success_per_5qi' },
    { header: 'VoNR Retainability (%)',       dataKey: 'vonr_retainability_percent', isPercentage: true }
  ]
};

export const saUeCallDropRateCellChartConfig: SiteChartsConfig = {
  title: 'UE Call Drop Rate (NR Cell)',
  apiEndpoint: '/api/sa-cell/ue-call-drop-rate-percent',
  csvFilename: 'sa_ue_call_drop_rate_cell',
  chartImageFilename: 'sa_ue_call_drop_rate_cell_chart',
  series: [
    { name: 'UE Call Drops',            type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'ue_call_drops' },
    { name: 'UE Call Release Commands', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'ue_call_release_commands' },
    { name: 'UE Call Drop Rate %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'ue_call_drop_rate_percent', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',            position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Call Drop Rate %', position: 'left',  nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'UE Call Drops',            dataKey: 'ue_call_drops' },
    { header: 'UE Call Release Commands', dataKey: 'ue_call_release_commands' },
    { header: 'UE Call Drop Rate (%)',    dataKey: 'ue_call_drop_rate_percent', isPercentage: true }
  ]
};

// --- Handover (5 charts) ---

export const saInterCuHandoutCellChartConfig: SiteChartsConfig = {
  title: 'Inter-CU Handout (NR Cell)',
  apiEndpoint: '/api/sa-cell/inter-cu-handout-success-rate-percent',
  csvFilename: 'sa_inter_cu_handout_cell',
  chartImageFilename: 'sa_inter_cu_handout_cell_chart',
  series: [
    { name: 'Inter-CU Handout Attempts',  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'inter_cu_ho_cn_attempts' },
    { name: 'Inter-CU Handout Successes', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'inter_cu_ho_ue_success' },
    { name: 'Inter-CU Handout SR %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'inter_cu_handout_successrate', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',          position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'Inter-CU Handout Attempts',  dataKey: 'inter_cu_ho_cn_attempts' },
    { header: 'Inter-CU Handout Successes', dataKey: 'inter_cu_ho_ue_success' },
    { header: 'Inter-CU Handout SR (%)',    dataKey: 'inter_cu_handout_successrate', isPercentage: true }
  ]
};

export const saInterCuHandinCellChartConfig: SiteChartsConfig = {
  title: 'Inter-CU Handin (NR Cell)',
  apiEndpoint: '/api/sa-cell/inter-cu-handin-success-rate-percent',
  csvFilename: 'sa_inter_cu_handin_cell',
  chartImageFilename: 'sa_inter_cu_handin_cell_chart',
  series: [
    { name: 'Inter-CU Handin Attempts',  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'inter_cu_hi_attempts' },
    { name: 'Inter-CU Handin Successes', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'inter_cu_hi_success' },
    { name: 'Inter-CU Handin SR %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'inter_cu_handin_successrate', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',          position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'Inter-CU Handin Attempts',  dataKey: 'inter_cu_hi_attempts' },
    { header: 'Inter-CU Handin Successes', dataKey: 'inter_cu_hi_success' },
    { header: 'Inter-CU Handin SR (%)',    dataKey: 'inter_cu_handin_successrate', isPercentage: true }
  ]
};

export const saInterCuIntraFreqHandoutCellChartConfig: SiteChartsConfig = {
  title: 'Inter-CU Intra-Freq Handout (NR Cell)',
  apiEndpoint: '/api/sa-cell/inter-cu-intra-freq-handout-success-rate-percent',
  csvFilename: 'sa_inter_cu_intra_freq_handout_cell',
  chartImageFilename: 'sa_inter_cu_intra_freq_handout_cell_chart',
  series: [
    { name: 'Inter-CU Intra-Freq Handout Attempts',  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'inter_cu_intra_freq_ho_cn_attempts' },
    { name: 'Inter-CU Intra-Freq Handout Successes', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'inter_cu_intra_freq_ho_ue_success' },
    { name: 'Inter-CU Intra-Freq Handout SR %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'inter_cu_intra_freq_handout_successrate', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',          position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'Intra-Freq Handout Attempts',  dataKey: 'inter_cu_intra_freq_ho_cn_attempts' },
    { header: 'Intra-Freq Handout Successes', dataKey: 'inter_cu_intra_freq_ho_ue_success' },
    { header: 'Intra-Freq Handout SR (%)',    dataKey: 'inter_cu_intra_freq_handout_successrate', isPercentage: true }
  ]
};

export const saInterCuInterFreqHandoutCellChartConfig: SiteChartsConfig = {
  title: 'Inter-CU Inter-Freq Handout (NR Cell)',
  apiEndpoint: '/api/sa-cell/inter-cu-inter-freq-handout-success-rate-percent',
  csvFilename: 'sa_inter_cu_inter_freq_handout_cell',
  chartImageFilename: 'sa_inter_cu_inter_freq_handout_cell_chart',
  series: [
    { name: 'Inter-CU Inter-Freq Handout Attempts',  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'inter_cu_inter_freq_ho_cn_attempts' },
    { name: 'Inter-CU Inter-Freq Handout Successes', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'inter_cu_inter_freq_ho_ue_success' },
    { name: 'Inter-CU Inter-Freq Handout SR %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'inter_cu_inter_freq_handout_successrate', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',          position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'Inter-Freq Handout Attempts',  dataKey: 'inter_cu_inter_freq_ho_cn_attempts' },
    { header: 'Inter-Freq Handout Successes', dataKey: 'inter_cu_inter_freq_ho_ue_success' },
    { header: 'Inter-Freq Handout SR (%)',    dataKey: 'inter_cu_inter_freq_handout_successrate', isPercentage: true }
  ]
};

export const saIntraCuHandoverCellChartConfig: SiteChartsConfig = {
  title: 'Intra-CU Handover (NR Cell)',
  apiEndpoint: '/api/sa-cell/intra-cu-handover-success-rate-percent',
  csvFilename: 'sa_intra_cu_handover_cell',
  chartImageFilename: 'sa_intra_cu_handover_cell_chart',
  series: [
    { name: 'Intra-CU HO Attempts',  type: 'bar',  yAxisIndex: 0, color: 'warning', dataKey: 'intra_cu_ho_attempts' },
    { name: 'Intra-CU HO Successes', type: 'bar',  yAxisIndex: 0, color: 'success', dataKey: 'intra_cu_ho_success' },
    { name: 'Intra-CU HO SR %',      type: 'line', yAxisIndex: 1, color: 'total',   dataKey: 'intra_cu_handover_successrate', isPercentage: true }
  ],
  yAxes: [
    { name: 'Count',          position: 'right', nameGap: 50, formatter: '{value}' },
    { name: 'Success Rate %', position: 'left',  min: 0, max: 100, nameGap: 50, formatter: '{value}%' }
  ],
  tableColumns: [
    { header: 'Intra-CU HO Attempts',  dataKey: 'intra_cu_ho_attempts' },
    { header: 'Intra-CU HO Successes', dataKey: 'intra_cu_ho_success' },
    { header: 'Intra-CU HO SR (%)',    dataKey: 'intra_cu_handover_successrate', isPercentage: true }
  ]
};

// --- Data Volume (1 chart) ---

export const saGtpDataVolumeCellChartConfig: SiteChartsConfig = {
  title: 'GTP Data Volume (NR Cell)',
  apiEndpoint: '/api/sa-cell/gtp-data-volume',
  csvFilename: 'sa_gtp_data_volume_cell',
  chartImageFilename: 'sa_gtp_data_volume_cell_chart',
  series: [
    { name: 'DL GTP Data Volume (MB)', type: 'bar', yAxisIndex: 0, color: 'info',    dataKey: 'dl_gtp_data_volume_mb' },
    { name: 'UL GTP Data Volume (MB)', type: 'bar', yAxisIndex: 0, color: 'warning', dataKey: 'ul_gtp_data_volume_mb' }
  ],
  yAxes: [
    { name: 'Volume (MB)', position: 'left',  nameGap: 60, formatter: '{value}' },
    { name: '',            position: 'right', formatter: '' }
  ],
  tableColumns: [
    { header: 'DL GTP (MB)', dataKey: 'dl_gtp_data_volume_mb' },
    { header: 'UL GTP (MB)', dataKey: 'ul_gtp_data_volume_mb' }
  ]
};
